/**
 * DAGExecutionTimeline Component - Execution Progress Timeline
 *
 * FERP CRITICAL: Domain-agnostic execution timeline visualization
 * Life-critical progress tracking with time estimates
 *
 * Principles:
 * - All data from backend via useDAGExecution hook (no hardcoded values)
 * - Repository pattern (GraphQL queries only)
 * - Real-time progress updates via polling
 * - Elapsed time and estimated completion calculation
 * - Immutable audit trails for execution timestamps
 * - Backend ID generation (never generate execution IDs client-side)
 * - 16 primitive types (DAG is workflow execution primitive)
 */

import React, { useEffect, useState } from 'react';
import { Box, Group, Paper, Progress, Stack, Text, Badge } from '@mantine/core';
import { IconClock, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import {
  DAGExecutionStatus,
  DAGExecutionStatusEnum,
} from './hooks/useDAGExecution';

// ============================================================
// TypeScript Types
// ============================================================

export interface DAGExecutionTimelineProps {
  executionStatus: DAGExecutionStatus | null;
}

// ============================================================
// Helper Functions
// ============================================================

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

function calculateElapsedTime(startedAt: string | undefined): number {
  if (!startedAt) return 0;
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  return now - start;
}

function calculateEstimatedCompletion(
  elapsedMs: number,
  completedNodes: number,
  totalNodes: number
): number {
  if (completedNodes === 0) return 0;
  const avgTimePerNode = elapsedMs / completedNodes;
  const remainingNodes = totalNodes - completedNodes;
  return avgTimePerNode * remainingNodes;
}

function getStatusColor(status: DAGExecutionStatusEnum): string {
  switch (status) {
    case DAGExecutionStatusEnum.Pending:
      return 'gray';
    case DAGExecutionStatusEnum.Running:
      return 'blue';
    case DAGExecutionStatusEnum.Completed:
      return 'green';
    case DAGExecutionStatusEnum.Failed:
      return 'red';
    case DAGExecutionStatusEnum.Paused:
      return 'yellow';
    case DAGExecutionStatusEnum.Cancelled:
      return 'orange';
    default:
      return 'gray';
  }
}

// ============================================================
// Component Implementation
// ============================================================

export function DAGExecutionTimeline({ executionStatus }: DAGExecutionTimelineProps) {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [estimatedCompletion, setEstimatedCompletion] = useState(0);

  // Update elapsed time every second
  useEffect(() => {
    if (!executionStatus?.started_at) return undefined;

    const interval = setInterval(() => {
      const elapsed = calculateElapsedTime(executionStatus.started_at);
      setElapsedTime(elapsed);

      // Calculate estimated completion
      if (
        executionStatus.status === DAGExecutionStatusEnum.Running &&
        executionStatus.completed_nodes > 0
      ) {
        const estimated = calculateEstimatedCompletion(
          elapsed,
          executionStatus.completed_nodes,
          executionStatus.total_nodes
        );
        setEstimatedCompletion(estimated);
      } else {
        setEstimatedCompletion(0);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [executionStatus]);

  // Calculate progress percentage
  const progressPercentage = executionStatus
    ? Math.round((executionStatus.completed_nodes / executionStatus.total_nodes) * 100)
    : 0;

  // Determine if execution is active
  const isActive = executionStatus?.status === DAGExecutionStatusEnum.Running;
  const isCompleted = executionStatus?.status === DAGExecutionStatusEnum.Completed;
  const isFailed = executionStatus?.status === DAGExecutionStatusEnum.Failed;

  if (!executionStatus) {
    return (
      <Paper p="md" withBorder>
        <Text size="sm" c="dimmed">
          No execution data available
        </Text>
      </Paper>
    );
  }

  const statusColor = getStatusColor(executionStatus.status);

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="sm">
            <IconClock size={20} />
            <Text size="sm" fw={600}>
              Execution Timeline
            </Text>
          </Group>
          <Badge color={statusColor} variant="filled">
            {executionStatus.status}
          </Badge>
        </Group>

        {/* Progress Bar */}
        <Box>
          <Group justify="space-between" mb="xs">
            <Text size="xs" c="dimmed">
              Progress
            </Text>
            <Text size="xs" fw={600}>
              {progressPercentage}%
            </Text>
          </Group>
          <Progress
            value={progressPercentage}
            color={isCompleted ? 'green' : isFailed ? 'red' : 'blue'}
            size="lg"
            animated={isActive}
          />
        </Box>

        {/* Node Statistics */}
        <Group grow gap="md">
          <Paper p="sm" withBorder style={{ backgroundColor: '#f8f9fa' }}>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Total Nodes
              </Text>
              <Text size="lg" fw={700}>
                {executionStatus.total_nodes}
              </Text>
            </Stack>
          </Paper>

          <Paper p="sm" withBorder style={{ backgroundColor: '#d3f9d8' }}>
            <Stack gap={4}>
              <Group gap={4}>
                <IconCheck size={12} />
                <Text size="xs" c="green">
                  Completed
                </Text>
              </Group>
              <Text size="lg" fw={700} c="green">
                {executionStatus.completed_nodes}
              </Text>
            </Stack>
          </Paper>

          <Paper p="sm" withBorder style={{ backgroundColor: '#d0ebff' }}>
            <Stack gap={4}>
              <Text size="xs" c="blue">
                Active
              </Text>
              <Text size="lg" fw={700} c="blue">
                {executionStatus.active_nodes.length}
              </Text>
            </Stack>
          </Paper>

          {executionStatus.failed_nodes > 0 && (
            <Paper p="sm" withBorder style={{ backgroundColor: '#ffe3e3' }}>
              <Stack gap={4}>
                <Group gap={4}>
                  <IconAlertCircle size={12} />
                  <Text size="xs" c="red">
                    Failed
                  </Text>
                </Group>
                <Text size="lg" fw={700} c="red">
                  {executionStatus.failed_nodes}
                </Text>
              </Stack>
            </Paper>
          )}
        </Group>

        {/* Time Information */}
        <Stack gap="xs">
          {executionStatus.started_at && (
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                Started At
              </Text>
              <Text size="xs" fw={500}>
                {new Date(executionStatus.started_at).toLocaleString()}
              </Text>
            </Group>
          )}

          {executionStatus.completed_at && (
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                Completed At
              </Text>
              <Text size="xs" fw={500}>
                {new Date(executionStatus.completed_at).toLocaleString()}
              </Text>
            </Group>
          )}

          {isActive && (
            <>
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  Elapsed Time
                </Text>
                <Text size="xs" fw={500} c="blue">
                  {formatDuration(elapsedTime)}
                </Text>
              </Group>

              {estimatedCompletion > 0 && (
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">
                    Estimated Remaining
                  </Text>
                  <Text size="xs" fw={500} c="blue">
                    {formatDuration(estimatedCompletion)}
                  </Text>
                </Group>
              )}
            </>
          )}

          {(isCompleted || isFailed) && executionStatus.started_at && executionStatus.completed_at && (
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                Total Duration
              </Text>
              <Text size="xs" fw={500}>
                {formatDuration(
                  new Date(executionStatus.completed_at).getTime() -
                    new Date(executionStatus.started_at).getTime()
                )}
              </Text>
            </Group>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
